/** EFFET : écran du mode solo (miroir déformant). */
import { grimaceAt, type GrimaceName } from '../ar/grimaces';
import { deformFace, MIXED, mirrorIntensity } from '../ar/mirrorDeform';
import { createMirrorRenderer } from '../ar/mirrorRenderer';
import { openCamera } from '../detection/faceTracker';
import { createExpressionMonitor } from '../detection/expressionMonitor';
import type { CameraFaceSource } from '../detection/faceTracker';
import { createLocalFaceSource } from '../detection/localFaceSource';
import { profileFor } from '../detection/profiles';
import type { FaceFrame } from '../detection/types';
import { DEFAULT_INCIDENT_THRESHOLDS } from '../game/incidents';
import { formatSeconds } from '../solo/format';
import { createRecordBook, type SoloMode } from '../solo/records';
import {
  createSoloState,
  elapsedPlayMs,
  soloStep,
  type SoloEvent,
  type SoloState,
} from '../solo/soloSession';
import { h, mount } from './dom';
import { calibrationMessage, TEXTS } from './messages';

const COUNTDOWN_S = 3;
const CALIBRATION_RETRY_MS = 2500;
const FACE_LOST_MS = DEFAULT_INCIDENT_THRESHOLDS.noFaceMs;

function safeStorage(): Storage | null {
  try {
    return localStorage;
  } catch {
    return null;
  }
}

export function renderSolo(root: HTMLElement, onMenu: () => void): () => void {
  const records = createRecordBook(safeStorage());
  let cleanupGame: (() => void) | null = null;

  const intro = (): void => {
    let selected: SoloMode = 'laugh';
    const bestLine = h('p', { class: 'muted', testid: 'solo-best' });
    const showBest = (): void => {
      const b = records.best(selected);
      bestLine.textContent = `Ton record : ${b === null ? '—' : formatSeconds(b)}`;
    };
    const mode = (value: SoloMode, label: string) => {
      const input = h('input', {
        type: 'radio',
        name: 'solo-mode',
        value,
        testid: `solo-mode-${value}`,
      });
      input.checked = value === selected;
      input.addEventListener('change', () => {
        selected = value;
        showBest();
      });
      return h('label', {}, input, label);
    };
    showBest();
    const start = h(
      'button',
      { type: 'button', class: 'button', testid: 'solo-start' },
      'Commencer',
    );
    const back = h('button', { type: 'button', class: 'button secondary' }, '← Menu');
    back.addEventListener('click', onMenu);
    mount(
      root,
      h('h1', { class: 'title' }, 'Le miroir'),
      h(
        'p',
        { class: 'intertitle' },
        'Regarde-toi dans le miroir… il va te ',
        h('strong', {}, 'déformer'),
        ' de plus en plus. Tiens le plus longtemps possible sans craquer !',
      ),
      h(
        'fieldset',
        { class: 'segmented' },
        h('legend', {}, 'Le premier qui…'),
        mode('laugh', 'rit'),
        mode('blink', 'cligne'),
      ),
      bestLine,
      h('div', { class: 'actions' }, back, start),
    );
    start.addEventListener('click', () => {
      void play(selected).catch((e: unknown) => {
        mount(
          root,
          h(
            'p',
            { class: 'status error', role: 'status', testid: 'status' },
            e instanceof Error ? e.message : String(e),
          ),
        );
      });
    });
  };

  const play = async (mode: SoloMode): Promise<void> => {
    const stream = await openCamera();
    const video = h('video', {
      autoplay: '',
      muted: '',
      playsinline: '',
      class: 'offscreen-video',
    });
    video.muted = true;
    video.srcObject = stream;
    const canvas = h('canvas', { class: 'mirror', testid: 'mirror' });
    const overlay = h('div', { class: 'mirror-overlay', testid: 'solo-overlay', role: 'status' });
    const chrono = h('p', { class: 'chrono', testid: 'chrono' }, formatSeconds(0));
    /** Nom de la grimace en cours (une nouvelle toutes les 4 s, ordre tiré au sort). */
    const grimace = h('p', { class: 'grimace', testid: 'grimace', 'aria-live': 'polite' });
    const best = h('p', { class: 'muted' });
    const result = h('div', { class: 'card result', testid: 'solo-result', hidden: '' });
    const stopButton = h(
      'button',
      { type: 'button', class: 'button secondary', testid: 'solo-stop' },
      'Arrêter',
    );
    mount(
      root,
      h('div', { class: 'mirror-wrap' }, canvas, overlay),
      chrono,
      grimace,
      best,
      stopButton,
      result,
      video,
    );

    const renderer = createMirrorRenderer(canvas);
    const source = createLocalFaceSource(video);
    const monitor = createExpressionMonitor(profileFor(mode), { calibrationMs: 3000 });
    let calibrationFps: number | undefined;
    let state: SoloState = soloStep(createSoloState(mode), { type: 'start', mode });
    let lastFaceAt = performance.now();
    let frozenIntensity = 0;
    let frozenWeights = MIXED;
    const seed = Math.floor(Math.random() * 2 ** 31);
    const showGrimace = (name: GrimaceName | null, label = ''): void => {
      if ((grimace.dataset['name'] ?? null) === name) return;
      if (name) grimace.dataset['name'] = name;
      else delete grimace.dataset['name'];
      grimace.textContent = label;
    };
    const timers = new Set<ReturnType<typeof setTimeout>>();
    const later = (ms: number, fn: () => void): void => {
      const id = setTimeout(() => {
        timers.delete(id);
        fn();
      }, ms);
      timers.add(id);
    };

    const dispatch = (event: SoloEvent): void => {
      const previous = state;
      state = soloStep(state, event);
      if (state === previous) return;
      if (state.phase === 'over' && previous.phase !== 'over') showResult();
      updateUi();
    };

    const startCountdown = (): void => {
      let n = COUNTDOWN_S;
      overlay.textContent = String(n);
      overlay.className = 'mirror-overlay countdown';
      const tick = (): void => {
        n -= 1;
        if (n > 0) {
          overlay.textContent = String(n);
          later(1000, tick);
          return;
        }
        // Détecteur réarmé : un rire commencé pendant le décompte sera vu.
        if (monitor.calibration) monitor.setCalibration(monitor.calibration);
        dispatch({ type: 'countdownDone', t: performance.now() });
      };
      later(1000, tick);
    };

    const showResult = (): void => {
      const held = state.heldMs ?? 0;
      const { isRecord, best: bestMs } = records.submit(state.mode, held);
      const again = h(
        'button',
        { type: 'button', class: 'button', testid: 'solo-again' },
        'Rejouer',
      );
      const menu = h('button', { type: 'button', class: 'button secondary' }, 'Menu');
      again.addEventListener('click', () => {
        dispatch({ type: 'restart' });
        monitor.calibrate(performance.now());
      });
      menu.addEventListener('click', () => {
        stop();
        intro();
      });
      result.replaceChildren(
        h('h2', {}, TEXTS[state.mode].lost),
        h('p', { class: 'big-time', testid: 'solo-held' }, `au bout de ${formatSeconds(held)}`),
        isRecord
          ? h('p', { class: 'record', testid: 'solo-record' }, 'Nouveau record !')
          : h('p', { class: 'muted' }, `Ton record : ${formatSeconds(bestMs ?? 0)}`),
        h('div', { class: 'actions' }, menu, again),
      );
    };

    const updateUi = (): void => {
      result.hidden = state.phase !== 'over';
      stopButton.hidden = state.phase === 'over';
      const b = records.best(state.mode);
      best.textContent = b === null ? 'Pas encore de record' : `Record : ${formatSeconds(b)}`;
      switch (state.phase) {
        case 'calibrating':
          overlay.className = 'mirror-overlay';
          overlay.textContent = state.calibrationError
            ? calibrationMessage(state.mode, state.calibrationError, calibrationFps)
            : TEXTS[state.mode].calibrating;
          break;
        case 'countdown':
          startCountdown();
          break;
        case 'playing':
          overlay.className = 'mirror-overlay hidden';
          overlay.textContent = '';
          break;
        case 'paused':
          overlay.className = 'mirror-overlay';
          overlay.textContent = 'On ne te voit plus… reviens dans le cadre !';
          break;
        default:
          overlay.className = 'mirror-overlay hidden';
      }
    };

    const onFrame = (frame: FaceFrame): void => {
      const now = frame.t;
      for (const e of monitor.push(frame)) {
        if (e.type === 'calibrated') dispatch({ type: 'calibrated' });
        else if (e.type === 'calibrationFailed') {
          calibrationFps = e.fps;
          dispatch({ type: 'calibrationFailed', error: e.error });
          later(CALIBRATION_RETRY_MS, () => {
            monitor.calibrate(performance.now());
          });
        } else dispatch({ type: 'lost', t: e.t });
      }

      if (frame.blendshapes) {
        lastFaceAt = now;
        if (state.phase === 'paused') dispatch({ type: 'faceBack', t: now });
      } else if (state.phase === 'playing' && now - lastFaceAt > FACE_LOST_MS) {
        dispatch({ type: 'faceLost', t: now });
      }

      const playingNow = state.phase === 'playing' || state.phase === 'paused';
      if (playingNow) {
        const elapsed = elapsedPlayMs(state, now);
        frozenIntensity = mirrorIntensity(elapsed);
        const g = grimaceAt(elapsed, seed);
        frozenWeights = g.weights;
        showGrimace(g.name, g.label);
      } else if (state.phase !== 'over') showGrimace(null);
      const intensity = playingNow || state.phase === 'over' ? frozenIntensity : 0;
      const landmarks = frame.landmarks ?? null;
      renderer.draw(
        frame.image ?? video,
        landmarks,
        landmarks && deformFace(landmarks, intensity, now, frozenWeights),
      );
      chrono.textContent = formatSeconds(elapsedPlayMs(state, now));
    };

    const off = source.subscribe(onFrame);
    const stop = (): void => {
      off();
      // Webcam : MediaPipe (worker) déchargé avec la partie ; une nouvelle partie le recharge.
      if ('close' in source) (source as CameraFaceSource).close();
      else source.stop();
      for (const id of timers) clearTimeout(id);
      for (const track of stream.getTracks()) track.stop();
      cleanupGame = null;
    };
    cleanupGame = stop;
    stopButton.addEventListener('click', () => {
      stop();
      intro();
    });

    updateUi();
    await source.start();
    monitor.calibrate(performance.now());
  };

  intro();
  return () => cleanupGame?.();
}
